/**
 * Per-TRAINEE body-measurement customization (admin/trainee-measurement).
 *
 * Distinct from the two neighbouring services:
 *   - MeasurementDefinitionService.js → the COMPANY catalog (affects everyone)
 *   - BodyMeasurementsService.js      → per-client measurement VALUES
 * This one is per-client measurement *configuration*: which measurements this one trainee has,
 * in which order.
 *
 * Keeps the {success, data, message} wrapper shape of MeasurementDefinitionService so the screen
 * can show a Hebrew toast per action rather than try/catching every call.
 *
 * No `companyId` argument anywhere — company is derived from the authenticated principal, and the
 * trainee scope is proven server-side by @authz.canAccessTrainee.
 *
 * Endpoints:
 *   GET    admin/trainee-measurement/{traineeId}/grid                          list (incl. hidden)
 *   PUT    admin/trainee-measurement/{traineeId}/reorder                       bulk reorder
 *   POST   admin/trainee-measurement/{traineeId}/definition/{id}/hide          hide (keeps history)
 *   POST   admin/trainee-measurement/{traineeId}/definition/{id}/unhide        un-hide
 *   POST   admin/trainee-measurement/{traineeId}/personal                      add personal
 *   PUT    admin/trainee-measurement/{traineeId}/personal/{id}                 edit personal
 *   DELETE admin/trainee-measurement/{traineeId}/personal/{id}                 PERMANENT delete
 */

import {
    adminTraineeMeasurementControllerGetGrid,
    adminTraineeMeasurementControllerReorder,
    adminTraineeMeasurementControllerHide,
    adminTraineeMeasurementControllerUnhide,
    adminTraineeMeasurementControllerCreatePersonal,
    adminTraineeMeasurementControllerUpdatePersonal,
    adminTraineeMeasurementControllerDeletePersonal,
} from '../api/generated';
import type {
    TraineeMeasurementItemDto,
    SavePersonalMeasurementRequest,
} from '../api/generated/types.gen';
import {getApiErrorMessage} from '../utils/apiError';

/** One row in the trainer's per-trainee list. Re-exported so components don't reach into generated/. */
export type TraineeMeasurementItem = TraineeMeasurementItemDto;

export type PersonalMeasurementPayload = SavePersonalMeasurementRequest;

/** {definitionId, displayOrder} pairs — the WHOLE list, hidden rows included. */
export interface ReorderItem {
    definitionId: number;
    displayOrder: number;
}

interface Result<T> {
    success: boolean;
    data?: T;
    message?: string;
}

/**
 * The generated client surfaces API errors on `error`; unwrap to the shared wrapper shape.
 *
 * Delegates to `getApiErrorMessage`. The local version this replaced read only
 * `response.data.message`, which the generated client never produces — it throws the parsed error
 * BODY, and the server puts its text under `errorMessage`. So every failure here used to collapse
 * into the generic fallback, including the one that most needs its own words: hiding a measurement
 * that נוסחת היקפים computes from is refused with a 409 that names the formula.
 */
const errorMessage = (error: unknown, fallback: string): string =>
    getApiErrorMessage(error, fallback);

export const getTraineeMeasurementGrid = async (
    traineeId: number,
): Promise<Result<TraineeMeasurementItem[]>> => {
    try {
        const {data, error} = await adminTraineeMeasurementControllerGetGrid({
            path: {traineeId},
        });
        if (error) throw error;
        return {success: true, data: data || []};
    } catch (error) {
        console.error('Error fetching trainee measurement grid:', error);
        return {
            success: false,
            message: errorMessage(error, 'שגיאה בטעינת מדדי המתאמן'),
            data: [],
        };
    }
};

/**
 * Persist a new order. `items` must contain the ENTIRE list including hidden rows — dropping them
 * orphans their order and makes them jump to the end when un-hidden.
 */
export const reorderTraineeMeasurements = async (
    traineeId: number,
    items: ReorderItem[],
): Promise<Result<void>> => {
    try {
        const {error} = await adminTraineeMeasurementControllerReorder({
            path: {traineeId},
            body: {items},
        });
        if (error) throw error;
        return {success: true};
    } catch (error) {
        console.error('Error reordering trainee measurements:', error);
        return {success: false, message: errorMessage(error, 'שגיאה בשמירת הסדר')};
    }
};

export const hideTraineeMeasurement = async (
    traineeId: number,
    definitionId: number,
): Promise<Result<void>> => {
    try {
        const {error} = await adminTraineeMeasurementControllerHide({
            path: {traineeId, definitionId},
        });
        if (error) throw error;
        return {success: true, message: 'המדד הוסתר'};
    } catch (error) {
        console.error('Error hiding trainee measurement:', error);
        return {success: false, message: errorMessage(error, 'שגיאה בהסתרת המדד')};
    }
};

export const unhideTraineeMeasurement = async (
    traineeId: number,
    definitionId: number,
): Promise<Result<void>> => {
    try {
        const {error} = await adminTraineeMeasurementControllerUnhide({
            path: {traineeId, definitionId},
        });
        if (error) throw error;
        return {success: true, message: 'המדד הוצג מחדש'};
    } catch (error) {
        console.error('Error un-hiding trainee measurement:', error);
        return {success: false, message: errorMessage(error, 'שגיאה בהצגת המדד')};
    }
};

export const createPersonalMeasurement = async (
    traineeId: number,
    payload: PersonalMeasurementPayload,
): Promise<Result<TraineeMeasurementItem>> => {
    try {
        const {data, error} = await adminTraineeMeasurementControllerCreatePersonal({
            path: {traineeId},
            body: payload,
        });
        if (error) throw error;
        return {success: true, data, message: 'המדד האישי נוצר בהצלחה'};
    } catch (error) {
        console.error('Error creating personal measurement:', error);
        return {success: false, message: errorMessage(error, 'שגיאה ביצירת המדד האישי')};
    }
};

export const updatePersonalMeasurement = async (
    traineeId: number,
    definitionId: number,
    payload: PersonalMeasurementPayload,
): Promise<Result<TraineeMeasurementItem>> => {
    try {
        const {data, error} = await adminTraineeMeasurementControllerUpdatePersonal({
            path: {traineeId, definitionId},
            body: payload,
        });
        if (error) throw error;
        return {success: true, data, message: 'המדד האישי עודכן'};
    } catch (error) {
        console.error('Error updating personal measurement:', error);
        return {success: false, message: errorMessage(error, 'שגיאה בעדכון המדד האישי')};
    }
};

/** PERMANENT — deletes the measurement and every value recorded for it. */
export const deletePersonalMeasurement = async (
    traineeId: number,
    definitionId: number,
): Promise<Result<void>> => {
    try {
        const {error} = await adminTraineeMeasurementControllerDeletePersonal({
            path: {traineeId, definitionId},
        });
        if (error) throw error;
        return {success: true, message: 'המדד האישי נמחק'};
    } catch (error) {
        console.error('Error deleting personal measurement:', error);
        return {success: false, message: errorMessage(error, 'שגיאה במחיקת המדד האישי')};
    }
};
